import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, message, Popconfirm, Row, Space, Table, Tag, Tooltip } from "antd";
import { TruncatedText } from "components/truncatedText";
import PacketingRuleModal from "features/packetingRulesTable/packetingRuleModal";
import {
  useDeletePacketingRuleMutation,
  useFetchPacketingRulesQuery,
} from "features/packetingRulesTable/packetingRulesAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import {
  firstPriorityFields,
  secondPriorityFields,
  thirdPriorityFields,
} from "features/mergeGroups/constants";
import { useState } from "react";
import styled from "styled-components";
import { useFetchAllDebtorUdfCustomFieldsQuery } from "features/userDefinedFields/agencyPortal/debtorUserDefinedFieldsAPI";

const StyledTable = styled(Table)`
  margin-top: 1em;
`;
const StyledRow = styled(Row)`
  margin-bottom: 1em;
`;

export default function PacketingRulesTable() {
  const [isModalVisible, setModalVisible] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const {
    data: {
      [PERMISSIONS.PACKETING_RULE__CREATE]: canCreatePacketingRule,
      [PERMISSIONS.PACKETING_RULE__DELETE]: canDeletePacketingRule,
      [PERMISSIONS.PACKETING_RULE__UPDATE]: canUpdatePacketingRule,
    },
  } = useAuthorizations();
  const { data: packetingRules, isLoading } = useFetchPacketingRulesQuery();
  const [deletePacketingRule] = useDeletePacketingRuleMutation();
  const { data: debtorCustomFields = [] } = useFetchAllDebtorUdfCustomFieldsQuery();
  const getCustomFieldDisplayName = (fieldSlug) => {
    const customField = debtorCustomFields.find((f) => f.slug === fieldSlug);
    return customField?.name || fieldSlug;
  };
  const allFields = [
    ...firstPriorityFields,
    ...secondPriorityFields,
    ...thirdPriorityFields,
  ].filter((field) => field.value !== "debtor_udf");

  const handleDelete = async (record) => {
    const result = await deletePacketingRule({ id: record.id });
    if ("data" in result) {
      message.success("Deleted packeting rule successfully.");
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Rule Summary",
      key: "summary",
      render: (text, record) => (
        <Col>
          <StyledRow>
            <Space size="small">
              <span>Identifier Match Params: </span>
              <span>
                {record.identifierMatchParams.map((param) => (
                  <Tag color="green" key={param}>
                    {allFields.find((field) => field.value === param)?.label ?? param}
                  </Tag>
                ))}
              </span>
            </Space>
          </StyledRow>
          <StyledRow>
            <Space size="small">
              <span>Identifier Disqualifying Params: </span>
              <span>
                {record.disqualifyingMatchParams.map((param) => (
                  <Tag color="red" key={param}>
                    {allFields.find((field) => field.value === param)?.label ?? param}
                  </Tag>
                ))}
              </span>
            </Space>
          </StyledRow>
          {Object.keys(record.partialMatchParamsScore ?? {}).length > 0 && (
            <StyledRow>
              <Space size="small">
                <span>Partial Match Params With Scores: </span>
                <span>
                  {(record.partialMatchParamsScore ?? []).map((score) => {
                    let displayName;

                    if (score.criteria === "debtor_udf" && score.fieldName) {
                      displayName = getCustomFieldDisplayName(score.fieldName);
                    } else {
                      displayName =
                        allFields.find((field) => field.value === score.criteria)?.label ??
                        score.criteria;
                    }

                    return (
                      <Tag
                        color="blue"
                        key={`${score.criteria}-${score.fieldName || ""}-${score.weight}`}
                      >
                        {displayName}: {score.weight}
                      </Tag>
                    );
                  })}
                </span>
              </Space>
            </StyledRow>
          )}
        </Col>
      ),
    },
    {
      title: "Description",
      dataIndex: "notes",
      key: "notes",
      render: (notes) => <TruncatedText text={notes} maxLength={40} />,
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Space size="middle">
          {canUpdatePacketingRule && (
            <Tooltip placement="bottom" title="Edit" key="edit">
              <EditOutlined
                key="edit"
                onClick={() => {
                  setActiveRecord(record);
                  setModalVisible(true);
                }}
              />
            </Tooltip>
          )}
          {canDeletePacketingRule && (
            <Popconfirm
              placement="topLeft"
              okText="Yes"
              title="Are you sure you want to delete this packeting rule?"
              onConfirm={() => handleDelete(record)}
            >
              <DeleteOutlined key="delete-packeting-rule" />
            </Popconfirm>
          )}
        </Space>
      ),
      fixed: "right",
    },
  ];

  return (
    <>
      <h4>Packeting Rules</h4>
      <Row align="middle">
        Packeting rules define how the accounts and debtors are packeted together.
        {canCreatePacketingRule && (
          <Button onClick={() => setModalVisible(true)} type="link" icon={<PlusOutlined />}>
            Add Packeting Rule
          </Button>
        )}
      </Row>
      <StyledTable
        loading={isLoading}
        bordered
        scroll={{ x: "max-content" }}
        // @ts-ignore
        columns={columns}
        dataSource={packetingRules}
      />
      {isModalVisible && (
        <PacketingRuleModal
          activeRecord={activeRecord}
          title={activeRecord ? "Edit Packeting Rule" : "Add Packeting Rule"}
          onOk={() => {
            setModalVisible(false);
            setActiveRecord(null);
          }}
          onCancel={() => {
            setModalVisible(false);
            setActiveRecord(null);
          }}
        />
      )}
    </>
  );
}
